import type React from "react";
import { useCallback, useMemo } from "react";

import { useNavigate } from "react-router";

import { ensureHttpsProtocol } from "simplydo/core/linkHref";
import { openDownloadLink } from "simplydo/core/storage";

import api from "api";

import { useCurrentUser } from "../../hooks";
import EditorWrapper from "./editor";
import type { EditorSettings, InnerEditorProps, Uploader } from "./editorTypes";
import { DefaultCapabilities, buildExtensions } from "./extensions";
import type { FileAttrs } from "./extensions/file";
import type { EditorCapabilities, UploadFileFn } from "./types";
import ReactTextEditor from "./view";

export type EditorProps = {
  capabilities?: EditorCapabilities;
  children?: React.ReactNode;
  disableBorder?: boolean;
  autoFocus?: boolean;

  settings?: EditorSettings;
  /** Shows undo/redo toolbar buttons only — TipTap history is always enabled. */
  historyEnabled?: boolean;
} & Omit<InnerEditorProps, "children" | "extensions">;

const localRegex = new RegExp(
  `((^| )https://(([a-z]{1,15}.)?simplydo.co.uk(/| |$)))`,
  "gi",
);
const devRegex = new RegExp(
  `((^| )(http://|https://)(([a-z]{1,15}.)?localhost(/| |$)))`,
  "gi",
);

const checkLocalLink = (href: string) => {
  if (href.startsWith("/")) {
    return { isLocal: true, path: href };
  }

  const localMatch = href.match(localRegex);
  if (localMatch) {
    return { isLocal: true, path: `/${href.replace(localRegex, "")}` };
  }
  const devMatch = href.match(devRegex);
  if (devMatch) {
    return { isLocal: true, path: `/${href.replace(devRegex, "")}` };
  }
  return { isLocal: false, path: href };
};

const createUploader = (
  uploadFile: UploadFileFn | undefined,
  getSettings: () => EditorSettings,
): Uploader | null => {
  if (!uploadFile) {
    return null;
  }

  return (file: File) => {
    const settings = getSettings();
    const upload = (settings.uploadFile ?? uploadFile) as UploadFileFn;
    const uploadOptions = settings.uploadOptions;
    if (!uploadOptions) {
      return Promise.reject(new Error("Uploads are not configured"));
    }
    return upload(file, uploadOptions).then((src) => ({
      src,
      name: file.name,
    }));
  };
};

export const RichTextEditor = ({
  value,
  onChange,
  onFocus,
  onBlur,
  emptyReturnValue,
  capabilities = DefaultCapabilities,
  readOnly = false,
  placeholder = null,
  "aria-label": ariaLabel,
  disableBorder,
  settings,
  editorRef,
  markdown,
  children,
  autoFocus,
}: EditorProps) => {
  const navigate = useNavigate();
  const user = useCurrentUser(api);

  const onLinkClick = useCallback(
    (href: string) => {
      // Links stay editable text while the editor is in use; only viewers navigate.
      if (!readOnly) {
        return false;
      }

      if (href.startsWith("#")) {
        const anchor = document.getElementById(href.slice(1));
        if (anchor) {
          anchor.scrollIntoView({ behavior: "smooth" });
        }
        return true;
      }

      const localLink = checkLocalLink(href);
      if (localLink.isLocal) {
        navigate(localLink.path);
      } else if (!href.startsWith("mailto:") && settings?.onExternalLink) {
        settings.onExternalLink(ensureHttpsProtocol(href));
      } else {
        window.open(ensureHttpsProtocol(href), "_blank");
      }

      return true;
    },
    [navigate, readOnly, settings],
  );

  const uploaders = useMemo(() => {
    const getSettings = () => settings ?? {};
    return {
      uploadImage: createUploader(capabilities.image?.uploadFile, getSettings),
      uploadFile: createUploader(capabilities.file?.uploadFile, getSettings),
    };
  }, [capabilities, settings]);

  const extensions = useMemo(() => {
    const onFileClick = (src: string, attrs: FileAttrs) => {
      const presetOnClick = capabilities.file?.onClick;

      if (!user || !src) {
        presetOnClick?.(src, attrs);
        return;
      }

      settings?.onFileClick?.(src);

      const urlData = new URL(src);
      const parameters = urlData.searchParams;
      api.uploads.getFileDownloadCookies(
        {
          forType: parameters.get("forType") ?? undefined,
          forId: parameters.get("forId") ?? undefined,
          entityType: parameters.get("entityType") ?? undefined,
          entityId: parameters.get("entityId") ?? undefined,
          identifier: parameters.get("identifier") ?? undefined,
        },
        ({ url }) => {
          openDownloadLink(url, attrs.name);
        },
        (error) => {
          console.error("Error getting file download cookies: ", error);
        },
      );
    };

    return buildExtensions(capabilities, {
      onFileClick,
      hasMentionRenderer: !!settings?.renderMention,
      placeholder: () => placeholder ?? "",
    });
  }, [user, capabilities, settings, placeholder]);

  return (
    <EditorWrapper
      placeholder={placeholder}
      aria-label={ariaLabel}
      readOnly={readOnly}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      onFocus={onFocus}
      emptyReturnValue={emptyReturnValue}
      extensions={extensions}
      editorRef={editorRef}
      autoFocus={autoFocus}
      markdown={markdown}
      settings={settings}
      onLinkClick={onLinkClick}
      uploadImage={uploaders.uploadImage}
      uploadFile={uploaders.uploadFile}
    >
      {children}
      <ReactTextEditor disableBorder={disableBorder} />
    </EditorWrapper>
  );
};

export type { EditorSettings, RenderMentionArgs } from "./editorTypes";
export type { EditorCapabilities } from "./types";
